import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TopBarContainer,
  TopBarContent,
  LogoSection,
  SearchSection,
  SearchInput,
  SearchIcon,
  ActionsSection,
  ActionButton,
  ProfileButton,
  ProfileAvatar,
  MobileMenuButton,
} from './style';
import { Logo } from '@components/Logo';
import { SearchResultsList } from './SearchResultsList';
import { FaBars, FaBell, FaQuestionCircle, FaSearch } from 'react-icons/fa';
import { AuthContext } from '@context/AuthContext';
import { FarmContext } from '@context/FarmContext';
import { getFirstAndLastName } from '@utils/functions';
import { appRoutes } from '@routes/appRoutes';
import { navigationItems, navigationSections } from '@components/navigation/config';
import { getVisibleNavigationItems, searchNavigationItems } from '@components/navigation/utils';
import { helpAnchor } from '@pages/help/model/anchors';
import { helpStore } from '@pages/help/runtime/helpStore';

interface TopNavigationProps {
  onMenuToggle: () => void;
  onNavigate: () => void;
}

export function TopNavigation({ onMenuToggle, onNavigate }: TopNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useContext(AuthContext);
  const { farm, licenseMode, licenseFeatures } = useContext(FarmContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLFormElement>(null);

  const userInitials = user?.name.substring(0, 2).toUpperCase() ?? 'U';
  const userName = getFirstAndLastName(user?.name ?? '');

  const visibleNavigationItems = useMemo(
    () =>
      getVisibleNavigationItems(navigationItems, {
        isAuthenticated,
        user,
        farm,
        licenseMode,
        licenseFeatures,
      }),
    [farm, isAuthenticated, licenseMode, licenseFeatures, user],
  );

  const sectionLabelMap = useMemo(
    () => new Map(navigationSections.map((section) => [section.key, section.label])),
    [],
  );

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    return searchNavigationItems(visibleNavigationItems, searchQuery)
      .map((item) => ({
        ...item,
        categoryLabel: sectionLabelMap.get(item.section) ?? item.section,
      }))
      .slice(0, 8);
  }, [searchQuery, sectionLabelMap, visibleNavigationItems]);

  const handleResultClick = useCallback(
    (route: string) => {
      navigate(route);
      setSearchQuery('');
      setIsSearchFocused(false);
      onNavigate();
    },
    [navigate, onNavigate],
  );

  const handleSearchSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const firstResult = searchResults[0];
      if (firstResult) {
        handleResultClick(firstResult.route);
      }
    },
    [handleResultClick, searchResults],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    if (isSearchFocused) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }

    return undefined;
  }, [isSearchFocused]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && !isSearchFocused && document.activeElement?.tagName !== 'INPUT') {
        event.preventDefault();
        const searchInput = document.querySelector<HTMLInputElement>('[data-search-input]');
        searchInput?.focus();
        setIsSearchFocused(true);
      }

      if (event.key === 'Escape' && isSearchFocused) {
        setIsSearchFocused(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [isSearchFocused, location.pathname]);

  return (
    <TopBarContainer>
      <TopBarContent>
        <LogoSection to={appRoutes.dashboard}>
          <Logo size="medium" showText color="white" />
        </LogoSection>

        <SearchSection ref={searchRef} onSubmit={handleSearchSubmit}>
          <SearchIcon>
            <FaSearch />
          </SearchIcon>
          <SearchInput
            data-search-input
            type="text"
            placeholder="Buscar... (pressione / para focar)"
            value={searchQuery}
            onChange={(event) => { setSearchQuery(event.target.value); }}
            onFocus={() => { setIsSearchFocused(true); }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && searchResults.length > 0) {
                event.preventDefault();
                const firstResult = document.querySelector<HTMLElement>('[data-search-result]');
                firstResult?.focus();
              }

              if (event.key === 'Escape') {
                setIsSearchFocused(false);
                setSearchQuery('');
              }
            }}
          />
          {isSearchFocused && searchQuery.trim() ? (
            <SearchResultsList results={searchResults} onSelect={handleResultClick} />
          ) : null}
        </SearchSection>

        <ActionsSection>
          <ActionButton type="button" aria-label="Notificacoes">
            <FaBell />
          </ActionButton>

          <ActionButton
            type="button"
            aria-label="Ajuda desta tela"
            title="Ajuda desta tela"
            onClick={() => {
              helpStore.openContextual();
            }}
            {...helpAnchor('shell.ajuda')}
          >
            <FaQuestionCircle />
          </ActionButton>

          <ProfileButton aria-label="Perfil do usuario">
            <ProfileAvatar>{userInitials}</ProfileAvatar>
            <span>{userName}</span>
          </ProfileButton>
        </ActionsSection>

        <MobileMenuButton onClick={onMenuToggle} aria-label="Menu">
          <FaBars />
        </MobileMenuButton>
      </TopBarContent>
    </TopBarContainer>
  );
}
